import { Placement } from '@floating-ui/react-dom-interactions';
import Button from '@shared/components/Button/Button';
import DropdownButton from '@shared/components/DropdownButton/DropdownButton';
import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Help } from 'assets/icons/Help.svg';
import useTheme from 'contexts/ThemeProvider/useTheme';

interface HelpDropdownProps {
  isContrastVariant?: boolean;
  buttonClassName?: string;
  className?: string;
  helpText?: boolean;
  wrapperClassName?: string;
  openInPortal?: boolean;
  placement?: Placement;
  dropdownOffset?: number;
  flipCrossAxis?: boolean;
  dropdownContainerClassName?: string;
}

const dropdownPanelOuter = 'max-h-[calc(100vh-5rem)] overflow-hidden flex flex-col min-h-0';
const dropdownPanelInner = 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain';

const HelpDropdown = ({
  isContrastVariant,
  buttonClassName,
  helpText = false,
  wrapperClassName,
  className,
  openInPortal = false,
  placement = 'bottom-end',
  dropdownOffset = 0,
  flipCrossAxis,
  dropdownContainerClassName,
}: HelpDropdownProps) => {
  const { helpLink, privacyPolicyLink, termsOfServiceLink } = useTheme();
  const { t } = useTranslation();

  const buttonVariant = isContrastVariant ? 'buttonIconContrastTertiary' : 'buttonIconSecondaryTertiary';

  const options: DropdownSelectionProps[] = [
    { title: t('help'), link: helpLink },
    { title: t('privacy_policy'), link: privacyPolicyLink },
    { title: t('terms_of_service'), link: termsOfServiceLink },
  ];

  return (
    <DropdownButton
      className={cx('min-w-56 z-dropdown bg-white', dropdownPanelInner, className)}
      wrapperClassName={wrapperClassName}
      dropdownContainerClass={cx(dropdownPanelOuter, dropdownContainerClassName)}
      positionStrategy={openInPortal ? 'fixed' : 'absolute'}
      placement={placement}
      dropdownOffset={dropdownOffset}
      flipCrossAxis={flipCrossAxis}
      options={options}
      aria-label={t('help_menu')}
      shouldPreventPropogation={false}
      openInPortal={openInPortal}
    >
      <Button
        size="neutral"
        variant={buttonVariant}
        className={cx(
          '!rounded-xl',
          helpText
            ? 'flex items-center justify-start text-left p-2 md:p-2.5'
            : 'inline-flex items-center justify-center p-0 w-10 h-10 min-w-10 min-h-10',
          buttonClassName
        )}
        tabIndex={-1}
        aria-label={t('help_menu')}
      >
        <Help className="shrink-0 stroke-1.5 stroke-current block h-5 w-5" />
        {helpText && <p className="ml-2 text-gray-80 text-sm font-medium">{t('help')}</p>}
      </Button>
    </DropdownButton>
  );
};

export default HelpDropdown;
